import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useDisclosure } from "@chakra-ui/react";
import { isLoggedIn, refreshToken } from "../services/authService";
import { isTokenExpiringSoon, getTokenLifetimePercentage, getSecondsUntilExpiration, getTokenExpirationTime } from "../utils/tokenExpiration";
import {
  initializeActivityTracking,
  cleanupActivityTracking,
  isUserInactive,
  getLastActivityTime,
  updateActivity,
  isActivityTrackingInitialized,
  getInitializationStatus,
} from "../utils/activityTracker";
import SessionWarningModal from "../components/SessionWarningModal";

const SessionWarningContext = createContext();

export const useSessionWarning = () => {
  const context = useContext(SessionWarningContext);
  if (!context) {
    throw new Error("useSessionWarning must be used within SessionWarningProvider");
  }
  return context;
};

// Global reference to trigger modal from error handler
let globalSessionWarningTrigger = null;

export const triggerSessionWarningModal = () => {
  if (globalSessionWarningTrigger) {
    globalSessionWarningTrigger();
  }
};

export const SessionWarningProvider = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [hasShownWarning, setHasShownWarning] = useState(false);
  const checkIntervalRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const proactiveRefreshLastAtRef = useRef(0);
  const tokenCheckIntervalModeRef = useRef(null);

  // Detect actual token lifetime and adjust thresholds dynamically
  const [tokenLifetime, setTokenLifetime] = useState(null);
  const [warningThreshold, setWarningThreshold] = useState(60000); // Default 1 minute
  const [modalShowTime, setModalShowTime] = useState(2 * 60 * 1000); // Default 2 minutes

  // Set global reference so error handler can trigger modal
  React.useEffect(() => {
    globalSessionWarningTrigger = onOpen;
    return () => {
      globalSessionWarningTrigger = null;
    };
  }, [onOpen]);

  // Auto-detect token lifetime on component mount
  React.useEffect(() => {
    if (isLoggedIn()) {
      const timeUntilExpiration = getTokenExpirationTime();
      if (timeUntilExpiration) {
        const lifetimeSeconds = Math.floor(timeUntilExpiration / 1000);
        
        setTokenLifetime(lifetimeSeconds);
        
        // Adjust warning thresholds based on actual token lifetime
        // Show warning at 90% of lifetime (10% remaining buffer)
        const adjustedWarningThreshold = Math.floor(timeUntilExpiration * 0.1); // Last 10%
        const adjustedModalShowTime = Math.floor(timeUntilExpiration * 0.15); // Show modal at 15%
        
        setWarningThreshold(adjustedWarningThreshold);
        setModalShowTime(adjustedModalShowTime);
      }
    }
  }, []);
  // Token expiration thresholds (adaptive - will auto-detect token lifetime)
  const TOKEN_LIFETIME_MINUTES = 3; // Token expires in 3 minutes
  const WARNING_THRESHOLD = 60000; // 1 minute in milliseconds (show modal when 1 minute remaining)
  const MODAL_SHOW_TIME = 2 * 60 * 1000; // Show modal at 2 minutes (1 minute before expiration)
  const REFRESH_TIME = 1.5 * 60 * 1000; // Refresh at 1.5 minutes (50% of 3 minutes) for active users
  
  // Activity tracking
  const INACTIVITY_THRESHOLD = 2 * 60 * 1000; // 2 minutes of inactivity
  
  // Check intervals — avoid sub-second polling (was 200ms) which spams /token/refresh and triggers HTTP 429
  const TOKEN_CHECK_INTERVAL_CRITICAL = 4000; // When session is in warning window
  const TOKEN_CHECK_INTERVAL_NORMAL = 12000; // Default polling
  const PROACTIVE_REFRESH_MIN_GAP_MS = 60000; // At most one proactive refresh per minute
  const ACTIVITY_CHECK_INTERVAL = 30000; // Secondary proactive-refresh check

  const handleRefreshSuccess = () => {
    // Reset warning state after successful refresh
    setHasShownWarning(false);
    // Update activity timestamp (user clicked continue)
    updateActivity();
  };

  const handleLogout = () => {
    // Reset warning state on logout
    setHasShownWarning(false);
    onClose();
  };

  // Proactive token refresh when user is active (at 50% of token lifetime)
  const handleProactiveRefresh = async () => {
    if (!isLoggedIn()) return;

    try {
      const timeUntilExpiration = getTokenExpirationTime();
      
      if (timeUntilExpiration === null) {
        return;
      }

      const timeUntilExpirationMs = timeUntilExpiration;
      const secondsUntilExpiration = Math.floor(timeUntilExpirationMs / 1000);
      
      // Refresh if token expires in <= 1.5 minutes (50% of 3-minute token)
      if (timeUntilExpirationMs <= REFRESH_TIME) {
        const now = Date.now();
        if (now - proactiveRefreshLastAtRef.current < PROACTIVE_REFRESH_MIN_GAP_MS) {
          return;
        }
        proactiveRefreshLastAtRef.current = now;
        await refreshToken();
        updateActivity();
      }
    } catch (error) {
      // Proactive path: avoid log noise; refreshToken applies HTTP 429 backoff
    }
  };

  // Initialize activity tracking - run on mount and when login status changes
  useEffect(() => {
    // Initialize immediately if user is logged in
    if (isLoggedIn()) {
      initializeActivityTracking();
      updateActivity(); // Set initial activity timestamp
    }

    // Also check periodically in case login status changes
    const statusCheckInterval = setInterval(() => {
      if (isLoggedIn() && !isActivityTrackingInitialized()) {
        initializeActivityTracking();
        updateActivity();
      } else if (!isLoggedIn()) {
        cleanupActivityTracking();
      }
    }, 2000);

    return () => {
      cleanupActivityTracking();
      clearInterval(statusCheckInterval);
    };
  }, []);

  // Check token expiration and show warning if needed
  useEffect(() => {
    // Function to check token expiration and user activity
    const checkTokenExpiration = () => {
      if (!isLoggedIn()) {
        // Clear intervals if user is not logged in
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
        }
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
        return;
      }

      try {
        // Ensure activity tracking is initialized
        if (!isActivityTrackingInitialized() && isLoggedIn()) {
          initializeActivityTracking();
          updateActivity();
        }

        const timeUntilExpiration = getTokenExpirationTime();
        if (timeUntilExpiration === null) {
          // Token expired or invalid
          return;
        }

        // DETECT TOKEN LIFETIME ON FIRST RUN
        if (!tokenLifetime) {
          const lifetimeSeconds = Math.floor(timeUntilExpiration / 1000);
          
          setTokenLifetime(lifetimeSeconds);
          
          // Adjust warning thresholds based on actual token lifetime
          // Show warning at 90% of lifetime (10% remaining buffer)
          const adjustedWarningThreshold = Math.floor(timeUntilExpiration * 0.1); // Last 10%
          const adjustedModalShowTime = Math.floor(timeUntilExpiration * 0.15); // Show modal at 15%
          
          setWarningThreshold(adjustedWarningThreshold);
          setModalShowTime(adjustedModalShowTime);
        }

        const timeUntilExpirationMs = timeUntilExpiration;
        const secondsUntilExpiration = Math.floor(timeUntilExpirationMs / 1000);
        const minutesUntilExpiration = Math.floor(secondsUntilExpiration / 60);
        const secondsRemaining = secondsUntilExpiration % 60;

        const isExpiringSoon = timeUntilExpirationMs <= warningThreshold; // Using dynamic threshold
        const isInactive = isUserInactive(INACTIVITY_THRESHOLD);
        const timeSinceActivity = Math.floor((Date.now() - getLastActivityTime()) / 1000 / 60);
        const shouldShowModal = timeUntilExpirationMs <= modalShowTime; // Using dynamic threshold

        // Logic for 3-minute token:
        // 1. If token expires in ≤2 minutes (1 min remaining) AND user is inactive (≥2 min) → show modal
        // 2. If token expires in ≤1 minute AND user is active (recent activity) → refresh token
        // 3. If token is no longer expiring (was refreshed) → close modal if open
        
        if (shouldShowModal && isInactive) {
          // Token expires in ≤2 minutes (1 min remaining) AND user inactive (≥2 minutes) - show warning modal
          if (!hasShownWarning && !isOpen) {
            setHasShownWarning(true);
            onOpen();
          }
        } else if (isExpiringSoon && !isInactive) {
          // Token expiring soon (≤1 minute) BUT user has recent activity (within 2 minutes) - refresh proactively
          handleProactiveRefresh();
        } else if (isOpen && !shouldShowModal) {
          // Token was refreshed and no longer in warning window - close modal
          onClose();
          setHasShownWarning(false);
        }

        const criticalWindowMs = Math.max(modalShowTime * 2, 120000);
        const nextMode = timeUntilExpirationMs <= criticalWindowMs ? "critical" : "normal";
        if (tokenCheckIntervalModeRef.current !== nextMode) {
          tokenCheckIntervalModeRef.current = nextMode;
          if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
          }
          const ms =
            nextMode === "critical"
              ? TOKEN_CHECK_INTERVAL_CRITICAL
              : TOKEN_CHECK_INTERVAL_NORMAL;
          checkIntervalRef.current = setInterval(checkTokenExpiration, ms);
        }
      } catch (error) {
        // Silently handle errors during token expiration check
      }
    };

    // Check immediately ONLY if logged in
    if (isLoggedIn()) {
      checkTokenExpiration();
      if (!checkIntervalRef.current) {
        tokenCheckIntervalModeRef.current = "normal";
        checkIntervalRef.current = setInterval(
          checkTokenExpiration,
          TOKEN_CHECK_INTERVAL_NORMAL,
        );
      }
    }

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      tokenCheckIntervalModeRef.current = null;
    };
  }, [warningThreshold, modalShowTime]);

  useEffect(() => {
    if (!isLoggedIn()) {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      return;
    }

    // Function to check if user is active and refresh token proactively
    const checkAndRefresh = () => {
      if (!isLoggedIn()) {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
        return;
      }

      const isInactive = isUserInactive(INACTIVITY_THRESHOLD);
      const timeSinceLastActivity = Math.floor((Date.now() - getLastActivityTime()) / 1000 / 60);
      const timeUntilExpiration = getTokenExpirationTime();
      const timeUntilExpirationMs = timeUntilExpiration || 0;
      
      // Only refresh if user is active AND token expires in ≤1.5 minutes (50% of lifetime)
      if (!isInactive && timeUntilExpirationMs > 0 && timeUntilExpirationMs <= REFRESH_TIME) {
        handleProactiveRefresh();
      }
    };

    // Wait a bit to ensure activity tracking is initialized
    const initialDelay = setTimeout(() => {
      checkAndRefresh();
    }, 5000);

    // Set up interval to check every 30 seconds
    refreshIntervalRef.current = setInterval(checkAndRefresh, ACTIVITY_CHECK_INTERVAL);

    return () => {
      clearTimeout(initialDelay);
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // Reset warning state when modal closes (user clicked continue or logged out)
  useEffect(() => {
    if (!isOpen && hasShownWarning) {
      // Small delay to allow token refresh to complete
      const timer = setTimeout(() => {
        setHasShownWarning(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, hasShownWarning]);

  return (
    <SessionWarningContext.Provider value={{ isOpen, onOpen, onClose }}>
      {children}
      <SessionWarningModal
        isOpen={isOpen}
        onClose={onClose}
        onRefreshSuccess={handleRefreshSuccess}
        onLogout={handleLogout}
      />
    </SessionWarningContext.Provider>
  );
};

